import { FunctionComponent, useCallback, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CellClickedEvent, GridApi, GridOptions, ICellRendererParams, RowDoubleClickedEvent } from 'ag-grid-community';
import { Button } from '@mui/material';
import { BASE_GRID_OPTIONS, PoGrid } from '@/components/grid/PoGrid';
import LinkCellRenderer, { LinkCellRendererParams } from '@/components/grid/cells/LinkCellRenderer';
import ErrorLoadingDataAlert from '@/components/feedback/ErrorLoadingDataAlert';
import { ColDefOrGroup } from '@/lib/ag-grid/types';
import { RelativeDateCellRenderer } from '@/components/grid/cells/RelativeDateCellRenderer';
import { ActionBar } from '@/modules/application';
import { MoveAssetsToLocationModal } from '@/modules/locations/components/MoveAssetsToLocationModal';
import { LocationAssetModel } from '@/modules/locations/types/LocationAssetModel';
import { ColumnID } from '@/components/grid/column-ids';
import { useTranslation } from '@/lib';
import ChangeAssetTypeModal from '@/modules/locations/components/ChangeAssetTypeModal';

interface AssetsAtLocationGridProps {
  data?: LocationAssetModel[];
  isError: boolean;
  noBorder?: boolean;
  isLoading?: boolean;
  onAssetsMoved: () => void;
  onExportToExcelClicked: (gridApi: GridApi<LocationAssetModel> | undefined) => void;
}

export const AssetsAtLocationGrid: FunctionComponent<AssetsAtLocationGridProps> = ({
  data,
  isError,
  noBorder,
  isLoading,
  onAssetsMoved,
  onExportToExcelClicked,
}) => {
  const navigate = useNavigate();
  const [selectedRows, setSelectedRows] = useState<LocationAssetModel[]>([]);
  const [isMoveAssetModalOpen, setIsMoveAssetModalOpen] = useState(false);
  const [isChangeAssetTypeModalOpen, setIsChangeAssetTypeModalOpen] = useState(false);
  const [gridApi, setGridApi] = useState<GridApi<LocationAssetModel> | null>(null);
  const { t } = useTranslation();

  const onCellClicked = useCallback((params: CellClickedEvent<LocationAssetModel>) => {
    if (params.column.getColId() === ColumnID.SELECTION_CHECKBOX) {
      const node = params.node;
      node.setSelected(!node.isSelected());
    }
  }, []);

  const customGridOptions: GridOptions<LocationAssetModel> = useMemo(
    (): GridOptions<LocationAssetModel> => ({
      ...BASE_GRID_OPTIONS,
      getRowId: (params) => params.data.dto.assetId.toString(),
      onRowDoubleClicked(event: RowDoubleClickedEvent<LocationAssetModel, unknown>) {
        navigate(`/app/assets/${event.data?.dto.assetId}`);
      },
      suppressRowClickSelection: true,
      rowSelection: 'multiple',
      onCellClicked: (event) => {
        onCellClicked(event);
      },
      onSelectionChanged: (event) => {
        setSelectedRows(event.api.getSelectedRows());
      },
      onGridReady: (event) => {
        setGridApi(event.api);
      },
      getContextMenuItems: (params) => [
        'copy',
        'separator',
        {
          name: t('export_to_excel'),
          action: () => onExportToExcelClicked(params.api),
        },
      ],
    }),
    [onCellClicked, gridApi, onExportToExcelClicked],
  );

  const columns: ColDefOrGroup<LocationAssetModel>[] = useMemo(
    () => [
      {
        colId: ColumnID.SELECTION_CHECKBOX,
        checkboxSelection: true,
        headerCheckboxSelection: true,
        resizable: false,
        width: 40,
        minWidth: 40,
        maxWidth: 40,
        suppressColumnsToolPanel: true,
        suppressMenu: true,
        lockVisible: true,
        sortable: false,
      },
      {
        colId: ColumnID.ASSET_CODE,
        field: 'dto.assetCode',
        headerName: t('asset'),
        cellRenderer: LinkCellRenderer,
        cellRendererParams: (params: ICellRendererParams<LocationAssetModel>): LinkCellRendererParams => ({
          pathname: params.data?.dto.assetId ? `/app/assets/${params.data?.dto.assetId}` : undefined,
        }),
      },
      {
        colId: ColumnID.ASSET_TYPE_NAME,
        field: 'dto.assetTypeName',
        headerName: t('type'),
        cellRenderer: LinkCellRenderer,
        cellRendererParams: (params: ICellRendererParams<LocationAssetModel>): LinkCellRendererParams => ({
          pathname: params.data?.dto.assetTypeId ? `/app/configuration/assets/types/${params.data?.dto.assetTypeId}` : undefined,
        }),
      },
      {
        colId: ColumnID.DATE_ARRIVED_AT_LOCATION,
        field: 'dto.dateArrivedAtLocation',
        headerName: t('arrived'),
        cellRenderer: RelativeDateCellRenderer,
        flex: 1,
      },
    ],
    [],
  );

  function onMoveSelectedAssetsClicked() {
    setIsMoveAssetModalOpen(true);
  }

  function onMoveSelectedAssetsClosed() {
    setIsMoveAssetModalOpen(false);
    clearSelection();
  }

  function onChangeAssetTypeClicked() {
    setIsChangeAssetTypeModalOpen(true);
  }

  function onChangeAssetTypeClosed() {
    setIsChangeAssetTypeModalOpen(false);
    clearSelection();
  }

  function onAssetTypeChangedByGrid() {
    onAssetsMoved();
  }

  function clearSelection() {
    setSelectedRows([]);

    if (gridApi) {
      gridApi.deselectAll();
    }
  }

  function onAssetsMovedByGrid() {
    onAssetsMoved();
  }

  return (
    <div className="flex h-full flex-1 flex-grow flex-col">
      {isError ? (
        <ErrorLoadingDataAlert />
      ) : (
        <PoGrid
          noBorder={noBorder}
          isLoading={isLoading}
          colDefs={columns}
          rowData={data}
          gridOptions={customGridOptions}
          disableDefaultGridOptions
        />
      )}

      <ActionBar visible={selectedRows.length > 0}>
        <Button variant="outlined" color="primary" onClick={clearSelection}>
          {t('clear_selection')}
        </Button>
        <Button variant="contained" onClick={onMoveSelectedAssetsClicked}>
          {t('move_assets')}
        </Button>
        <Button variant="contained" onClick={onChangeAssetTypeClicked}>
          {t('change_asset_type')}
        </Button>
      </ActionBar>
      <MoveAssetsToLocationModal
        selectedAssetIds={selectedRows.map((lam) => lam.dto.assetId)}
        isOpen={isMoveAssetModalOpen}
        onClose={onMoveSelectedAssetsClosed}
        onAssetsMoved={onAssetsMovedByGrid}
      />
      <ChangeAssetTypeModal
        selectedAssetIds={selectedRows.map((lam) => lam.dto.assetId)}
        isOpen={isChangeAssetTypeModalOpen}
        onClose={onChangeAssetTypeClosed}
        onAssetsMoved={onAssetTypeChangedByGrid}
      />
    </div>
  );
};
